import { Plus, Trash2, GripVertical, Sparkles } from "lucide-react";
import { INTAKE_TEMPLATES, mergeTemplateQuestions } from "../lib/intakeTemplates";

/**
 * IntakeQuestionsEditor — Acuity-style intake form builder for a service.
 *
 * Providers add the questions their clients should answer when booking
 * (e.g. birthdate for astrology, target goal for candle work, dream text
 * for interpretation). Each question is a small record:
 *   { id, label, type, required, options?, help? }
 *
 * `id` stays stable across edits so answers stored on past bookings still
 * map back to their question label. We auto-generate an id from the label
 * only for freshly-added questions.
 */
const TYPES = [
  { value: "text", label: "Short text" },
  { value: "textarea", label: "Long text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "select", label: "Dropdown (one)" },
  { value: "multiselect", label: "Checkboxes (many)" },
];

function slug(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32) || `q-${Math.random().toString(36).slice(2, 6)}`;
}

export default function IntakeQuestionsEditor({ value, onChange, testIdPrefix = "intake" }) {
  const questions = Array.isArray(value) ? value : [];

  const setAt = (idx, patch) => {
    const next = questions.map((q, i) => (i === idx ? { ...q, ...patch } : q));
    onChange(next);
  };

  const addQuestion = () => {
    const base = { id: `q-${Date.now().toString(36)}`, label: "", type: "text", required: false };
    onChange([...questions, base]);
  };

  const removeAt = (idx) => onChange(questions.filter((_, i) => i !== idx));

  const applyTemplate = (tmpl) => {
    const merged = mergeTemplateQuestions(questions, tmpl.questions);
    onChange(merged);
  };

  const move = (idx, delta) => {
    const j = idx + delta;
    if (j < 0 || j >= questions.length) return;
    const next = [...questions];
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-editor`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="label-mystic text-secondary">Intake questions (optional)</div>
          <div className="text-[11px] text-foreground/55 mt-0.5">
            Clients answer these before payment. Answers land in your booking dashboard + email.
          </div>
        </div>
        <button
          type="button"
          onClick={addQuestion}
          className="border border-secondary/40 text-secondary hover:bg-secondary/10 px-3 py-1.5 rounded-sm uppercase tracking-wider text-[11px] inline-flex items-center gap-1.5"
          data-testid={`${testIdPrefix}-add`}
        >
          <Plus className="w-3 h-3" /> Add question
        </button>
      </div>

      {questions.length === 0 && (
        <div className="text-[11px] text-foreground/50 italic py-2">
          No intake questions yet. Add one to collect context before every session.
        </div>
      )}

      {/* ─── One-click templates ─────────────────────────────────
          Practitioners can paste a template's questions with a single
          tap. Repeated taps of the same template are safe — merge is
          id-deduped, so nothing gets duplicated. */}
      <div className="rounded-sm border border-dashed border-secondary/30 p-3" data-testid={`${testIdPrefix}-templates`}>
        <div className="flex items-center gap-1.5 mb-2">
          <Sparkles className="w-3 h-3 text-secondary" />
          <div className="text-[10px] uppercase tracking-wider text-secondary">Start from a template</div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {INTAKE_TEMPLATES.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => applyTemplate(t)}
              title={t.hint}
              className="border border-secondary/25 hover:border-secondary/60 hover:bg-secondary/10 px-2.5 py-1 rounded-sm text-[11px] transition"
              data-testid={`${testIdPrefix}-template-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {questions.map((q, i) => {
        const needsOptions = q.type === "select" || q.type === "multiselect";
        return (
          <div
            key={q.id || i}
            className="border border-secondary/20 rounded-sm p-3 space-y-2 bg-input/30"
            data-testid={`${testIdPrefix}-row-${i}`}
          >
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => move(i, -1)}
                disabled={i === 0}
                className="text-foreground/40 hover:text-foreground/70 disabled:opacity-25"
                data-testid={`${testIdPrefix}-up-${i}`}
                aria-label="Move up"
              >
                <GripVertical className="w-4 h-4" />
              </button>
              <input
                value={q.label}
                onChange={(e) => {
                  const label = e.target.value;
                  // Auto-fill id from label ONLY while the id looks
                  // auto-generated — never overwrite a stable id from
                  // an already-saved question.
                  const looksAuto = !q.id || q.id.startsWith("q-");
                  setAt(i, { label, id: looksAuto ? slug(label) : q.id });
                }}
                placeholder="Question label (e.g. Date of birth)"
                className="flex-1 bg-input border border-secondary/20 p-2 rounded-sm text-sm"
                data-testid={`${testIdPrefix}-label-${i}`}
                maxLength={200}
              />
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="text-destructive/80 hover:text-destructive p-1"
                data-testid={`${testIdPrefix}-remove-${i}`}
                aria-label="Remove question"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
              <select
                value={q.type}
                onChange={(e) => {
                  const type = e.target.value;
                  const patch = { type };
                  if (type !== "select" && type !== "multiselect") patch.options = undefined;
                  else if (!q.options?.length) patch.options = ["", ""];
                  setAt(i, patch);
                }}
                className="bg-input border border-secondary/20 p-2 rounded-sm text-sm"
                data-testid={`${testIdPrefix}-type-${i}`}
              >
                {TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>

              <label className="inline-flex items-center gap-2 text-xs text-foreground/75 px-2">
                <input
                  type="checkbox"
                  checked={!!q.required}
                  onChange={(e) => setAt(i, { required: e.target.checked })}
                  data-testid={`${testIdPrefix}-required-${i}`}
                />
                Required
              </label>
            </div>

            {needsOptions && (
              <div className="space-y-1.5" data-testid={`${testIdPrefix}-options-${i}`}>
                <div className="text-[10px] uppercase tracking-wider text-foreground/50">Options</div>
                {(q.options || []).map((opt, oi) => (
                  <div key={oi} className="flex gap-2">
                    <input
                      value={opt}
                      onChange={(e) => {
                        const next = [...(q.options || [])];
                        next[oi] = e.target.value;
                        setAt(i, { options: next });
                      }}
                      placeholder={`Option ${oi + 1}`}
                      className="flex-1 bg-input border border-secondary/20 p-1.5 rounded-sm text-sm"
                      data-testid={`${testIdPrefix}-option-${i}-${oi}`}
                      maxLength={80}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const next = (q.options || []).filter((_, idx) => idx !== oi);
                        setAt(i, { options: next.length ? next : ["", ""] });
                      }}
                      className="text-destructive/70 hover:text-destructive text-xs px-2"
                      data-testid={`${testIdPrefix}-option-remove-${i}-${oi}`}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setAt(i, { options: [...(q.options || []), ""] })}
                  className="text-secondary text-xs uppercase tracking-wider hover:underline"
                  data-testid={`${testIdPrefix}-option-add-${i}`}
                >
                  + Add option
                </button>
              </div>
            )}

            <input
              value={q.help || ""}
              onChange={(e) => setAt(i, { help: e.target.value })}
              placeholder="Helper text shown under the field (optional)"
              className="w-full bg-input border border-secondary/20 p-1.5 rounded-sm text-xs"
              data-testid={`${testIdPrefix}-help-${i}`}
              maxLength={300}
            />
          </div>
        );
      })}
    </div>
  );
}
